// utils/scores.js
import { db } from '../firebase';
import { collection, addDoc, query, orderBy, limit, getDocs, where, updateDoc, doc } from 'firebase/firestore';

// Status Constants
export const ScoreStatus = {
  SUCCESS: 'success',
  INVALID_NAME_LENGTH: 'invalid-name-length',
  INVALID_DAYS: 'invalid-days',
  INVALID_MONEY: 'invalid-money',
  INVALID_METRICS: 'invalid-metrics',
  SCORE_UPDATED: 'score-updated',
  LOWER_SCORE: 'lower-score',
  ERROR: 'error',
};

// Helper to compare scores
const isNewScoreBetter = (newScore, existingScore) => {
  if (newScore.daysSurvived > existingScore.daysSurvived) return true;
  if (newScore.daysSurvived === existingScore.daysSurvived && 
      newScore.moneyEarned > existingScore.moneyEarned) return true;
  return false;
};

export const saveScore = async (scoreData) => {
  try {
    // Validate constraints
    if (scoreData.playerName.trim().length > 30) {
      return { status: ScoreStatus.INVALID_NAME_LENGTH };
    }
    
    if (scoreData.daysSurvived > 30 || scoreData.daysSurvived <= 0) {
      return { status: ScoreStatus.INVALID_DAYS };
    }

    if (scoreData.moneyEarned >= 10000 || scoreData.moneyEarned < 0) {
      return { status: ScoreStatus.INVALID_MONEY };
    }

    if (scoreData.servedCustomers < 0 || scoreData.totalRevenue < 0) {
      return { status: ScoreStatus.INVALID_METRICS };
    }

    const cleanedData = {
      playerName: scoreData.playerName.trim(),
      daysSurvived: Number(scoreData.daysSurvived),
      moneyEarned: Number(scoreData.moneyEarned), 
      servedCustomers: Number(scoreData.servedCustomers),
      totalRevenue: Number(scoreData.totalRevenue),
      timestamp: new Date()
    };

    // Query for existing score with same name
    const q = query(
      collection(db, 'highscores'),
      where('playerName', '==', cleanedData.playerName)
    );
    const existingScores = await getDocs(q);

    if (!existingScores.empty) {
      const existingScore = existingScores.docs[0].data();
      
      if (isNewScoreBetter(cleanedData, existingScore)) {
        const scoreRef = doc(db, 'highscores', existingScores.docs[0].id);
        await updateDoc(scoreRef, cleanedData);
        cachedScores = null; // Invalidate cache
        return { status: ScoreStatus.SCORE_UPDATED };
      } else {
        return { status: ScoreStatus.LOWER_SCORE, existingScore };
      }
    }
    
    // No existing score found, save new one
    await addDoc(collection(db, 'highscores'), cleanedData);
    cachedScores = null; // Invalidate cache
    return { status: ScoreStatus.SUCCESS };

  } catch (error) {
    console.error('Error saving score:', error);
    return { status: ScoreStatus.ERROR, message: error.message };
  }
};

// Add caching
let cachedScores = null;
let lastFetch = 0;
const CACHE_DURATION = 60000; // 1 minute

export const getHighscores = async (forceRefresh = false) => {
  if (!forceRefresh && cachedScores && (Date.now() - lastFetch < CACHE_DURATION)) {
    return cachedScores;
  }

  try {
    const q = query(
      collection(db, 'highscores'),
      orderBy('daysSurvived', 'desc'),
      orderBy('moneyEarned', 'desc'),
      limit(10)
    );
    
    const snapshot = await getDocs(q);
    cachedScores = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    lastFetch = Date.now();
    return cachedScores;
  } catch (error) {
    console.error('Error fetching highscores:', error);
    return cachedScores || [];
  }
};